import { getAssistantGuide } from "../content/socraticGuide";
import type { SocraticStage } from "../../shared/sceneGuideCatalog";
import {
  describeLocalAssistantState,
  resolveConversationalAssistantReply,
  taskHelpLead
} from "../../shared/localAssistantReplies";
import type { AiDialogueMessage, AiRequest, AiResponse, FallbackReason, SceneId } from "../types";

type LocalGuidance = Pick<AiResponse, "message">;

function conversationalGuidance(request: AiRequest): LocalGuidance | null {
  const reply = resolveConversationalAssistantReply(request.studentText);
  return reply ? { message: reply.feedback } : null;
}

function resolveLocalStage(request: AiRequest): SocraticStage {
  const { snapshot } = request;
  if (snapshot.misconceptionTags.includes("scope-overreach")
    || snapshot.misconceptionTags.includes("expression-unclear")) return "revision";
  if (snapshot.misconceptionTags.includes("causality-overstated")) return "reasoning";
  if (snapshot.misconceptionTags.includes("evidence-missing")) return "evidence";
  if (snapshot.misconceptionTags.includes("concept-confusion")) return "claim";
  if (snapshot.phase === "revise") return "revision";
  if (snapshot.phase === "verify") return "evidence";
  if (snapshot.lastAction.result === "error" || snapshot.consecutiveErrors > 0) return "evidence";

  const priorStudentTurns = boundedRecentMessages(snapshot.recentMessages)
    .filter((message) => message.role === "user").length;
  if (snapshot.sceneId === "F02") {
    return priorStudentTurns === 0 ? "evidence" : priorStudentTurns === 1 ? "reasoning" : "revision";
  }
  if (priorStudentTurns === 0) return "claim";
  if (priorStudentTurns === 1) return "evidence";
  if (priorStudentTurns === 2) return "reasoning";
  return "revision";
}

function createStageGuidance(request: AiRequest, stage: SocraticStage): LocalGuidance {
  const { snapshot } = request;
  const guide = getAssistantGuide(snapshot.sceneId);
  const lead = taskHelpLead(request.studentText);
  const stateLead = describeLocalAssistantState(snapshot);
  const prefix = stateLead ? `${stateLead} ` : "";
  if (stage === "claim") {
    return { message: `${prefix}${lead}${guide.localFallback.feedback}` };
  }
  if (stage === "evidence") {
    if (snapshot.consecutiveErrors > 1) {
      return { message: `${prefix}连续 ${snapshot.consecutiveErrors} 次尝试仍遇到困难。${lead}${guide.localFallback.feedback}` };
    }
    if (snapshot.viewedEvidenceIds.length === 0) {
      return { message: `${prefix}可先查看 ${guide.evidenceIds.join("、") || "当前场景记录"}。${guide.localFallback.feedback}` };
    }
    return { message: `${prefix}${guide.localFallback.feedback}` };
  }
  if (stage === "reasoning") {
    return {
      message: snapshot.consecutiveErrors > 1
        ? `${prefix}连续尝试仍未形成证据链。${lead}${guide.localFallback.feedback}`
        : `${prefix}${lead}${guide.localFallback.feedback}`
    };
  }
  return { message: `${prefix}${lead}${guide.localFallback.feedback}` };
}

export function createLocalAiGuidance(request: AiRequest, fallbackReason?: FallbackReason): AiResponse {
  const conversational = conversationalGuidance(request);
  const guidance = conversational ?? createStageGuidance(request, resolveLocalStage(request));
  return {
    mode: "fallback",
    ...guidance,
    evidenceRefs: [],
    misconceptionTags: conversational ? [] : ["evidence-missing"],
    ...(fallbackReason ? { fallbackReason } : {})
  };
}

function boundedRecentMessages(messages: readonly AiDialogueMessage[] = []): AiDialogueMessage[] {
  return messages.flatMap((message) => {
    if (message.role !== "user" && message.role !== "assistant") return [];
    if (typeof message.content !== "string") return [];
    const content = message.content.trim().slice(0, message.role === "user" ? 120 : 180);
    return content ? [{ role: message.role, content }] : [];
  }).slice(-4);
}

function resolveInteractionType(sceneId: SceneId): "active-question" | "ai-audit" | "final-review" {
  if (sceneId === "F01") return "active-question";
  if (sceneId === "F02") return "ai-audit";
  if (sceneId === "F03") return "final-review";
  return "active-question";
}

export interface RequestAiOptions {
  signal?: AbortSignal;
  /** @deprecated 请求级密钥已停用；保留类型兼容但不会发送。 */
  apiKey?: string;
}

export class AiRequestAbortedError extends Error {
  constructor() {
    super("AI request was canceled");
    this.name = "AiRequestAbortedError";
  }
}

function fallbackForStatus(status: number): FallbackReason {
  if (status === 401 || status === 403) return "invalid-key";
  if (status === 429) return "rate-limited";
  if (status === 408 || status === 504) return "timeout";
  if (status === 400 || status === 422) return "invalid-context";
  return "upstream-error";
}

const FALLBACK_REASONS = new Set<FallbackReason>([
  "not-configured", "invalid-key", "rate-limited", "timeout", "invalid-context",
  "model-unavailable", "upstream-error", "invalid-response", "network-error"
]);
const ALLOWED_MISCONCEPTION_TAGS = new Set([
  "evidence-missing", "concept-confusion", "causality-overstated", "scope-overreach", "expression-unclear"
]);

function textValue(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const text = value.trim();
  return text || undefined;
}

function parseJson(value: string): unknown | undefined {
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return undefined;
  }
}

function recordValue(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
}

/** 接受直接 JSON、代码围栏 JSON，以及模型在 JSON 前后附加的说明文字。 */
function extractJsonLoose(value: string): unknown | undefined {
  const trimmed = value.trim();
  const direct = parseJson(trimmed);
  if (direct !== undefined) return direct;
  for (const match of trimmed.matchAll(/```(?:json)?\s*([\s\S]*?)\s*```/giu)) {
    const parsed = parseJson(match[1] ?? "");
    if (parsed !== undefined) return parsed;
  }
  for (let start = trimmed.indexOf("{"); start >= 0; start = trimmed.indexOf("{", start + 1)) {
    let depth = 0;
    let inString = false;
    let escaped = false;
    for (let index = start; index < trimmed.length; index += 1) {
      const character = trimmed[index];
      if (inString) {
        if (escaped) escaped = false;
        else if (character === "\\") escaped = true;
        else if (character === "\"") inString = false;
        continue;
      }
      if (character === "\"") inString = true;
      else if (character === "{") depth += 1;
      else if (character === "}") {
        depth -= 1;
        if (depth === 0) {
          const parsed = parseJson(trimmed.slice(start, index + 1));
          if (parsed !== undefined) return parsed;
          break;
        }
      }
    }
  }
  return undefined;
}

function unwrapPlainText(value: string): string {
  const fenced = value.trim().match(/^```(?:[a-z][\w-]*)?\s*([\s\S]*?)\s*```$/iu);
  return (fenced?.[1] ?? value).trim();
}

/** 将代理成功响应归一化；教学语义不在客户端二次审查。 */
function normalizeAiPayload(raw: string, sceneId: SceneId): AiResponse | null {
  const plain = unwrapPlainText(raw);
  if (!plain) return null;
  const structured = extractJsonLoose(plain);
  const record = recordValue(structured);
  const arrayText = Array.isArray(structured)
    ? structured.flatMap((item) => textValue(item) ? [String(item).trim()] : []).join("\n")
    : undefined;
  const message = (typeof structured === "string" ? textValue(structured) : undefined)
    ?? textValue(arrayText)
    ?? textValue(record?.feedback)
    ?? textValue(record?.message)
    ?? textValue(record?.answer)
    ?? textValue(record?.content)
    ?? textValue(record?.text)
    ?? (parseJson(plain) === undefined ? plain : undefined);
  if (!message) return null;

  const guide = getAssistantGuide(sceneId);
  const rawCitations = Array.isArray(record?.citations) ? record.citations : [];
  const evidenceRefs = [...new Set(rawCitations)]
    .filter((id): id is string => typeof id === "string")
    .filter((id) => guide.evidenceIds.includes(id))
    .slice(0, 4);
  const rawTags = Array.isArray(record?.misconceptionTags) ? record.misconceptionTags : [];
  const misconceptionTags = [...new Set(rawTags)]
    .filter((tag): tag is string => typeof tag === "string")
    .filter((tag) => ALLOWED_MISCONCEPTION_TAGS.has(tag))
    .slice(0, 3);
  const fallbackReason = textValue(record?.fallbackReason);
  const normalizedFallbackReason = fallbackReason && FALLBACK_REASONS.has(fallbackReason as FallbackReason)
    ? fallbackReason as FallbackReason
    : undefined;
  const source = record?.source === "local" ? "fallback" : "online";
  return {
    mode: source,
    message: [...message].slice(0, 2_000).join(""),
    evidenceRefs,
    misconceptionTags,
    ...(source === "fallback" && normalizedFallbackReason ? { fallbackReason: normalizedFallbackReason } : {})
  };
}

// 请求受控学习助手；网络异常时立即回退为本地章节提示。
export async function requestAiGuidance(request: AiRequest, options: RequestAiOptions = {}): Promise<AiResponse> {
  const controller = new AbortController();
  let timedOut = false;
  let callerAborted = options.signal?.aborted ?? false;
  const timer = globalThis.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, 8000);
  const abortFromCaller = () => {
    callerAborted = true;
    controller.abort();
  };
  if (callerAborted) controller.abort();
  options.signal?.addEventListener("abort", abortFromCaller, { once: true });

  try {
    const chapterId = request.chapterId === "quiz" ? "conclusion" : request.chapterId;
    const sceneId = request.snapshot.sceneId;
    const interactionType = resolveInteractionType(sceneId);
    const snapshot = {
      ...request.snapshot,
      recentMessages: boundedRecentMessages(request.snapshot.recentMessages)
    };

    const response = await fetch("/api/ai/respond", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        chapterId,
        interactionType,
        studentAnswer: request.studentText,
        snapshot
      }),
      signal: controller.signal
    });
    if (!response.ok) return createLocalAiGuidance(request, fallbackForStatus(response.status));
    let payloadText: string;
    try {
      payloadText = await response.text();
    } catch {
      return createLocalAiGuidance(request, "invalid-response");
    }
    const normalized = normalizeAiPayload(payloadText, sceneId);
    return normalized ?? createLocalAiGuidance(request, "invalid-response");
  } catch (error) {
    if (callerAborted) throw new AiRequestAbortedError();
    if (timedOut || (error instanceof Error && error.name === "AbortError")) {
      return createLocalAiGuidance(request, "timeout");
    }
    return createLocalAiGuidance(request, "network-error");
  } finally {
    globalThis.clearTimeout(timer);
    options.signal?.removeEventListener("abort", abortFromCaller);
  }
}
